import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";
import { FaHeadset, FaExclamationTriangle, FaGavel, FaInfoCircle } from "react-icons/fa";

const GrievanceRedressalPolicy: React.FC = () => {
    const companyName = import.meta.env.VITE_COMPANY_NAME || "EPOCH Private Limited";
    const companyEmail = import.meta.env.VITE_COMPANY_EMAIL || "support@epoch.com";

    const policySections = [
        {
            title: "1. Introduction",
            icon: <FaInfoCircle />,
            content: `At ${companyName}, customer satisfaction is our prime focus. We believe in providing the best services to our customers. To ensure that our customers are satisfied, we have a robust Grievance Redressal Mechanism in place.`,
        },
        {
            title: "2. Resolution Mechanism",
            icon: <FaHeadset />,
            content: "Customers experiencing any issues can reach out via the following levels of escalation:",
            list: [
                `Level 1: Contact our support team via email at ${companyEmail}.`,
                "Level 2: If unresolved within 7 days, escalate to the Grievance Redressal Officer.",
                "Level 3: If the issue remains unresolved after 30 days, customers may appeal to the NBFC Ombudsman at the Reserve Bank of India.",
            ],
        },
        {
            title: "3. Grievance Redressal Officer",
            icon: <FaExclamationTriangle />,
            content: "Our designated Grievance Redressal Officer handles all escalated complaints with a mandated turnaround time.",
            list: [
                "Name: NBFC Grievance Officer",
                `Email: ${companyEmail}`,
                "Turnaround Time: Maximum 30 working days from the date of receipt of the complaint.",
            ],
        },
        {
            title: "4. RBI Ombudsman",
            icon: <FaGavel />,
            content: "If you are not satisfied with the resolution provided by our Grievance Redressal Officer, you may escalate your complaint to the Reserve Bank of India (RBI) under the Integrated Ombudsman Scheme.",
        },
    ];

    return (
        <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
            <SEO
                title="Grievance Redressal"
                description={`We value your feedback. Understand the ${companyName} grievance resolution process and how we ensure your concerns are addressed.`}
                keywords={`grievance redressal, customer support, RBI ombudsman, fintech trust`}
            />
            <Navbar />

            <main className="relative z-10 pt-44 pb-20 md:pb-24">
                <div className="max-w-4xl mx-auto px-6 lg:px-12">

                    <header className="mb-24 text-center">
                        <div className="inline-flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl rounded-full mb-10 transition-transform hover:scale-105">
                            <div className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
                            <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">You're Heard.</span>
                        </div>
                        <h1 className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-10 text-text-primary">
                            We Listen <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">to You.</span>
                        </h1>
                        <p className="text-text-secondary text-lg md:text-2xl font-medium leading-relaxed max-w-2xl mx-auto">
                            Customer satisfaction is at the center of everything we do. If we fail to meet your expectations, we have a clear, three-tier process to set things right.
                        </p>
                    </header>

                    <div className="relative space-y-12">
                        {policySections.map((item, idx) => (
                            <div key={idx} className="bg-white p-12 md:p-16 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl z-10 group">
                                <div className="flex flex-col md:flex-row gap-12 items-start">
                                    <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center text-primary text-3xl shrink-0 group-hover:bg-primary group-hover:text-white transition-all duration-500 transform group-hover:-translate-y-2">
                                        {item.icon}
                                    </div>
                                    <div className="flex-1 space-y-6">
                                        <h2 className="text-3xl md:text-5xl font-black text-text-primary tracking-tighter">
                                            {item.title}
                                        </h2>
                                        {item.content && (
                                            <p className="text-text-secondary text-lg md:text-xl font-medium leading-relaxed">
                                                {item.content}
                                            </p>
                                        )}
                                        {item.list && (
                                            <ul className="space-y-4 pt-4 border-t border-gray-50">
                                                {item.list.map((li, i) => (
                                                    <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-lg leading-relaxed">
                                                        <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                                                        <span>{li}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Final Trust Note */}
                    <div className="mt-20 p-12 bg-primary text-white rounded-[3rem] shadow-2xl text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
                        <h3 className="text-2xl md:text-4xl font-black mb-6">Committed to Fair Resolution</h3>
                        <p className="text-white/80 text-lg md:text-xl leading-relaxed font-medium mb-10 max-w-2xl mx-auto">
                            Every complaint is an opportunity for us to improve. We guarantee a fair, speedy, and entirely transparent resolution for every user at {companyName}.
                        </p>
                        <div className="font-black text-xs uppercase tracking-[0.3em] text-secondary">Registered NBFC Accountability</div>
                    </div>

                </div>
            </main>

            <Footer />
        </div>
    );
};

export default GrievanceRedressalPolicy;
